import { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { disburseToUserWallet } from '../../../../services/AccountService';
import { readErrorEnvelope } from '../../../../utils/errorEnvelope';
import PinInput from '../../PinInput';

const TopUpUserWalletForm = ({ partnerId, partnerUserWalletBalances, partnerUserWalletBalancesLoading }) => {
  const queryClient = useQueryClient();

  const [currentUserBalance, setCurrentUserBalance] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(null);
  const [pinLockRetryAfterSeconds, setPinLockRetryAfterSeconds] = useState(null);

  const [formData, setFormData] = useState({
    user_unique_id: '',
    amount: '',
    transactionPin: '',
  });

  useEffect(() => {
    if (formData.user_unique_id) {
      const user = partnerUserWalletBalances?.find(user => user.user_unique_id === formData.user_unique_id);
      setCurrentUserBalance(user ? user.balance : null);
    } else {
      setCurrentUserBalance(null);
    }
  }, [formData, partnerUserWalletBalances]);

  useEffect(() => {
    if (formData.user_unique_id) return;
    if (partnerUserWalletBalancesLoading) return;
    if (partnerUserWalletBalances?.length !== 1) return;
    const soleWalletHolder = partnerUserWalletBalances[0];
    setFormData((prev) => ({ ...prev, user_unique_id: soleWalletHolder.user_unique_id }));
  }, [partnerUserWalletBalances, partnerUserWalletBalancesLoading, formData.user_unique_id]);

  const disburseMutation = useMutation({
    mutationFn: (data) => disburseToUserWallet(partnerId, data),
  });

  const resetForm = () => {
    setFormData({ user_unique_id: '', amount: '', transactionPin: '' });
    setCurrentUserBalance(null);
    setPinAttemptsRemaining(null);
    setPinLockRetryAfterSeconds(null);
  };

  const handleDisburseFunds = async (e) => {
    e.preventDefault();

    const payload = {
      user_unique_id: formData.user_unique_id,
      amount: formData.amount,
      transaction_pin: formData.transactionPin,
    };

    try {
      await toast.promise(
        disburseMutation.mutateAsync(payload),
        {
          pending: 'Processing request ...',
          success: 'Your request will be processed shortly.',
          error: {
            render({ data: err }) {
              return readErrorEnvelope(err).message;
            },
          },
        },
      );
      queryClient.invalidateQueries({ queryKey: ['partnerVaultBalance', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partnerUserWalletBalances', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['userWalletOutflowCount', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['topSpenders', partnerId] });
      resetForm();
    } catch (err) {
      const { errorCode, meta } = readErrorEnvelope(err);
      if (errorCode === 'PIN_INVALID') {
        setPinAttemptsRemaining(meta.remaining_attempts ?? null);
        setPinLockRetryAfterSeconds(null);
      } else if (errorCode === 'PIN_LOCKED') {
        setPinLockRetryAfterSeconds(meta.retry_after_seconds ?? null);
        setPinAttemptsRemaining(null);
      }
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const isAmountBelowMinimum = formData.amount !== '' && Number(formData.amount) < 5;
  const areTopUpFieldsFilled = formData.user_unique_id && formData.amount !== '' && Number(formData.amount) >= 5;
  const isFormValid = areTopUpFieldsFilled && formData.transactionPin;

  return (
    <>
      <form onSubmit={handleDisburseFunds}>
        <div className="sell-blance">
          <label className="form-label text-muted small">Select User</label>
          <div className="form-label blance">
            {partnerUserWalletBalancesLoading ? (
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
            ) : (
              <>
                {currentUserBalance ? (
                  <>
                    <span>WALLET BALANCE:</span>
                    <p>{currentUserBalance}</p>
                  </>
                ) : <></>}
              </>
            )}
          </div>
          <div className="form-group mb-3">
            {(() => {
              const selectedUser = partnerUserWalletBalances?.find(
                (user) => user.user_unique_id === formData.user_unique_id
              );
              const toggleLabel = partnerUserWalletBalancesLoading
                ? 'Loading users'
                : selectedUser
                  ? selectedUser.userName
                  : partnerUserWalletBalances?.length
                    ? 'Select a user'
                    : 'No users available';
              const noUsers = !partnerUserWalletBalancesLoading && !partnerUserWalletBalances?.length;
              const filteredUsers = partnerUserWalletBalances?.filter((user) =>
                user.userName?.toLowerCase().includes(userSearch.trim().toLowerCase())
              ) ?? [];
              return (
                <Dropdown className="w-100" onToggle={(isOpen) => { if (!isOpen) setUserSearch(''); }}>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="d-flex align-items-center w-100"
                    disabled={partnerUserWalletBalancesLoading || noUsers}
                  >
                    <span
                      className={`flex-grow-1 text-start ${selectedUser ? '' : 'text-muted'}`}
                      title={selectedUser ? toggleLabel : undefined}
                      style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {toggleLabel}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    <div className="px-2 pb-2 position-sticky top-0 bg-white" style={{ zIndex: 1 }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search users"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <Dropdown.Item
                          key={user.user_unique_id}
                          active={formData.user_unique_id === user.user_unique_id}
                          onClick={() => setFormData((prev) => ({ ...prev, user_unique_id: user.user_unique_id }))}
                          title={user.userName}
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {user.userName}
                        </Dropdown.Item>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-muted small">No users match your search.</div>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              );
            })()}
          </div>
        </div>
        <div className="sell-blance">
          <label className="form-label text-muted small" htmlFor="amount">Amount</label>
          <div className="input-group">
            <input type="number" className="form-control" placeholder="" id="amount" min="5" step="1" value={formData.amount} onChange={handleInputChange} />
          </div>
          {isAmountBelowMinimum && (
            <small className="text-danger mt-1 d-block">Minimum amount is KES 5.</small>
          )}
        </div>

        {areTopUpFieldsFilled && (
          <div className="sell-blance mt-3">
            <label className="form-label text-danger" htmlFor="transactionPin">
              Enter Transaction PIN
            </label>
            <div className="input-group">
              <PinInput
                placeholder="Enter your PIN"
                id="transactionPin"
                name="transactionPin"
                value={formData.transactionPin}
                onChange={handleInputChange}
              />
            </div>
            {pinAttemptsRemaining !== null && (
              <small className={`mt-1 d-block ${pinAttemptsRemaining <= 1 ? 'text-danger' : 'text-warning'}`}>
                {pinAttemptsRemaining === 0
                  ? 'No attempts remaining.'
                  : `${pinAttemptsRemaining} attempt${pinAttemptsRemaining === 1 ? '' : 's'} remaining.`}
              </small>
            )}
            {pinLockRetryAfterSeconds !== null && (
              <small className="mt-1 d-block text-danger">
                Your PIN is locked. Try again in {Math.ceil(pinLockRetryAfterSeconds / 60)} minute{Math.ceil(pinLockRetryAfterSeconds / 60) === 1 ? '' : 's'}.
              </small>
            )}
          </div>
        )}

        <div className="text-center">
          <button
            className="btn btn-primary w-75"
            type="submit"
            disabled={!isFormValid || disburseMutation.isPending}
          >
            {disburseMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing
              </>
            ) : (
              "Top Up"
            )}
          </button>
        </div>

      </form>
    </>
  )
}
export default TopUpUserWalletForm;
